import { ParamMap, Params } from '@angular/router';
import { IBase } from 'src/app/models';
import {
  SuggestionSortType,
  SuggestionUserResolutionFilter,
  SuggestionVisibility,
} from 'src/app/models/enums';
import { SuggestionPriority } from 'src/app/models/enums/suggestionPriority';
import { SuggestionStatus } from 'src/app/models/enums/suggestionStatus';
import { SuggestionType } from 'src/app/models/enums/suggestionType';
import {
  parseEnumList,
  parseIdList,
  parseNumberList,
  serializeIdList,
  serializeNumberList,
  serializeStringList,
} from './filterUtils';

export const DEFAULT_SUGGESTION_SORT = SuggestionSortType.LAST_MODIFICATION;

export interface ISuggestionsFilters {
  selectedClients: number[];
  selectedTowns: number[];
  selectedServices: IBase[];
  selectedTypes: SuggestionType[];
  selectedStatus: SuggestionStatus[];
  selectedPriorities: SuggestionPriority[];
  selectedUserResolutions: SuggestionUserResolutionFilter[];
  selectedVisibilities: SuggestionVisibility[];
  selectedExpeditions: IBase[];
}

export const SUGGESTION_FILTER_KEYS = [
  'clients', 'towns', 'services', 'types', 'status', 'priorities', 'resolutions', 'visibilities', 'expeditions'
] as const;

export function emptySuggestionsFilters(): ISuggestionsFilters {
  return toSuggestionsFilters({});
}

export function toSuggestionsFilters(source: Partial<Record<keyof ISuggestionsFilters, unknown>>): ISuggestionsFilters {
  const listOf = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);
  return {
    selectedClients: listOf(source.selectedClients),
    selectedTowns: listOf(source.selectedTowns),
    selectedServices: listOf(source.selectedServices),
    selectedTypes: listOf(source.selectedTypes),
    selectedStatus: listOf(source.selectedStatus),
    selectedPriorities: listOf(source.selectedPriorities),
    selectedUserResolutions: listOf(source.selectedUserResolutions),
    selectedVisibilities: listOf(source.selectedVisibilities),
    selectedExpeditions: listOf(source.selectedExpeditions),
  };
}

export function serializeSuggestionsFilters(f: ISuggestionsFilters): Params {
  return {
    clients: serializeNumberList(f.selectedClients),
    towns: serializeNumberList(f.selectedTowns),
    services: serializeIdList(f.selectedServices),
    types: serializeStringList(f.selectedTypes),
    status: serializeStringList(f.selectedStatus),
    priorities: serializeStringList(f.selectedPriorities),
    resolutions: serializeStringList(f.selectedUserResolutions),
    visibilities: serializeStringList(f.selectedVisibilities),
    expeditions: serializeIdList(f.selectedExpeditions),
  };
}

export function deserializeSuggestionsFilters(
  params: ParamMap,
  fallback?: ISuggestionsFilters
): ISuggestionsFilters {
  return {
    selectedClients: parseNumberList(params.get('clients')),
    selectedTowns: parseNumberList(params.get('towns')),
    selectedServices: parseIdList(params.get('services'), fallback?.selectedServices),
    selectedTypes: parseEnumList<SuggestionType>(params.get('types')),
    selectedStatus: parseEnumList<SuggestionStatus>(params.get('status')),
    selectedPriorities: parseEnumList<SuggestionPriority>(params.get('priorities')),
    selectedUserResolutions: parseEnumList<SuggestionUserResolutionFilter>(params.get('resolutions')),
    selectedVisibilities: parseEnumList<SuggestionVisibility>(params.get('visibilities')),
    selectedExpeditions: parseIdList(params.get('expeditions'), fallback?.selectedExpeditions),
  };
}

export function parseSuggestionsFiltersFromStorage(json: string | null | undefined): ISuggestionsFilters | null {
  if (!json) return null;
  try {
    return toSuggestionsFilters(JSON.parse(json));
  } catch {
    return null;
  }
}
